import React, { useState, useEffect, useContext, useRef } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  PiUserCircle,
  PiVideoCamera,
  PiFolderUser,
  PiFileVideoLight,
  PiChartBar,
  PiList,
  PiX,
  PiCrown,
  PiBookOpen,
  PiQuestion,
  PiStorefront
} from 'react-icons/pi';
import { FaChevronDown } from 'react-icons/fa';
import ChannelDiscoveryContext from '../../contexts/ChannelDiscoveryContext';
import {VideoDiscoveryContext} from '../../contexts/VideoDiscoveryContext';
import VideoCategorySelector from '../VideoDiscovery/VideoCategorySelector';
import VideoRankingMethodSelector from '../VideoDiscovery/VideoRankingMethodSelector';
import VideoRegionSelector from '../VideoDiscovery/VideoRegionSelector';
import TimeWindowSelector from '../VideoDiscovery/TimeWindowSelector';
import VideoTagsFilter from '../VideoDiscovery/VideoTagsFilter';
import CategorySelector from '../ChannelDiscovery/CategorySelector';
import RankingMethodSelector from '../ChannelDiscovery/RankingMethodSelector';
import ChannelRegionSelector from '../ChannelDiscovery/ChannelRegionSelector';
import TagsFilter from '../ChannelDiscovery/TagsFilter';
import './Sidebar.css';

/**
 * Sidebar компонент с упрощенной логикой под CSS Grid архитектуру
 * @param {boolean} isVisible - показать/скрыть sidebar (управляется Layout)
 * @param {boolean} isCollapsed - свернут ли sidebar (управляется Layout)
 * @param {function} onCollapsedChange - callback для изменения состояния сворачивания
 */
const Sidebar = ({ 
  isVisible = true, 
  isCollapsed = false,
  onCollapsedChange 
}) => {
  const { t } = useTranslation();
  
  // ✅ ИСПРАВЛЕНИЕ: Убираем внутреннее состояние isCollapsed - используем только внешнее
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [researchExpanded, setResearchExpanded] = useState(false);
  const [forceUpdate, setForceUpdate] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  // Ref для отслеживания скролла (fade эффекты)
  const navRef = useRef(null);

  const channelDiscoveryContext = useContext(ChannelDiscoveryContext);
  const videoDiscoveryContext = useContext(VideoDiscoveryContext);

  const {
    loading: channelLoading,
    loadingMore: channelLoadingMore,
    error: channelError
  } = channelDiscoveryContext || {};

  const {
    loading: videoLoading,
    loadingMore: videoLoadingMore,
    error: videoError
  } = videoDiscoveryContext || {};

  const isChannelLoading = channelLoading || channelLoadingMore;
  const isVideoLoading = videoLoading || videoLoadingMore;

  const navigate = useNavigate();
  const location = useLocation();

  // Проверка мобильного устройства
  const checkIsMobile = () => {
    return window.innerWidth <= 768;
  };

  // ✅ УПРОЩЕННЫЙ useEffect для мобильной адаптации
  useEffect(() => {
    const handleResize = () => {
      const newIsMobile = checkIsMobile();
      setIsMobile(newIsMobile);
      
      if (!newIsMobile && isMobileOpen) {
        setIsMobileOpen(false);
      }
      
      setForceUpdate(prev => prev + 1);
    };

    setIsMobile(checkIsMobile());
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isMobileOpen]);

  // Фильтры состояния (без изменений - весь существующий код)
  const [localVideoFilters, setLocalVideoFilters] = useState({
    selectedCategory: '',
    selectedFormat: '',
    selectedTags: [],
    selectedRegion: '',
    rankingMethod: 'views',
    timeWindow: 'week'
  });

  const [localChannelFilters, setLocalChannelFilters] = useState({
    selectedCategory: '',
    selectedSubcategory: '',
    selectedRegion: '',
    rankingMethod: 'subscribers',
    selectedTags: []
  });

  const [hasVideoChanges, setHasVideoChanges] = useState(false);
  const [hasChannelChanges, setHasChannelChanges] = useState(false);

  // Все контекстные данные (без изменений)
  const {
    categories: channelCategories,
    selectedCategory: channelSelectedCategory,
    selectedSubcategory: channelSelectedSubcategory,
    selectedRegion: channelSelectedRegion,
    rankingMethod: channelRankingMethod,
    selectedTags: channelSelectedTags,
    setSelectedCategory: setChannelSelectedCategory,
    setSelectedSubcategory: setChannelSelectedSubcategory,
    setSelectedRegion: setChannelSelectedRegion,
    setRankingMethod: setChannelRankingMethod,
    setSelectedTags: setChannelSelectedTags
  } = channelDiscoveryContext || {};

  const {
    categories: videoCategories,
    selectedCategory: videoSelectedCategory,
    selectedFormat: videoSelectedFormat,
    selectedTags: videoSelectedTags,
    selectedRegion: videoSelectedRegion,
    rankingMethod: videoRankingMethod,
    timeWindow: videoTimeWindow,
    setSelectedCategory: setVideoSelectedCategory,
    setSelectedFormat: setVideoSelectedFormat,
    setSelectedTags: setVideoSelectedTags,
    setSelectedRegion: setVideoSelectedRegion,
    setRankingMethod: setVideoRankingMethod,
    setTimeWindow: setVideoTimeWindow,
  } = videoDiscoveryContext || {};

  // Синхронизация фильтров (без изменений - весь существующий код)
  useEffect(() => {
    if (videoDiscoveryContext) {
      const newVideoFilters = {
        selectedCategory: videoSelectedCategory || '',
        selectedFormat: videoSelectedFormat || '',
        selectedTags: videoSelectedTags || [],
        selectedRegion: videoSelectedRegion || '',
        rankingMethod: videoRankingMethod || 'views',
        timeWindow: videoTimeWindow || 'week'
      };
      setLocalVideoFilters(newVideoFilters);
      setHasVideoChanges(false);
    }
  }, [
    videoSelectedCategory,
    videoSelectedFormat,
    videoSelectedTags,
    videoSelectedRegion,
    videoRankingMethod,
    videoTimeWindow,
    videoDiscoveryContext
  ]);

  useEffect(() => {
    if (channelDiscoveryContext) {
      const newChannelFilters = {
        selectedCategory: channelSelectedCategory || '',
        selectedSubcategory: channelSelectedSubcategory || '',
        selectedRegion: channelSelectedRegion || '',
        rankingMethod: channelRankingMethod || 'subscribers',
        selectedTags: channelSelectedTags || []
      };
      setLocalChannelFilters(newChannelFilters);
      setHasChannelChanges(false);
    }
  }, [
    channelSelectedCategory,
    channelSelectedSubcategory,
    channelSelectedRegion,
    channelRankingMethod,
    channelSelectedTags,
    channelDiscoveryContext
  ]);

  // Отслеживание скролла для fade эффектов
  const updateScrollState = () => {
    if (!navRef.current) return;
    
    const nav = navRef.current;
    const hasScroll = nav.scrollHeight > nav.clientHeight;
    nav.classList.toggle('has-scroll', hasScroll);
  };

  useEffect(() => {
    const nav = navRef.current;
    if (!nav) return;

    const handleScroll = () => {
      updateScrollState();
    };

    const resizeObserver = new ResizeObserver(() => {
      updateScrollState();
    });

    nav.addEventListener('scroll', handleScroll);
    resizeObserver.observe(nav);
    
    updateScrollState();

    return () => {
      nav.removeEventListener('scroll', handleScroll);
      resizeObserver.disconnect();
    };
  }, []);

  // Обработчики фильтров (без изменений - весь существующий код)
  const handleVideoFilterChange = (filterName, value) => {
    const newFilters = { ...localVideoFilters, [filterName]: value };
    
    setLocalVideoFilters(newFilters);
    
    const hasChanges = (
      newFilters.selectedCategory !== (videoSelectedCategory || '') ||
      newFilters.selectedFormat !== (videoSelectedFormat || '') ||
      JSON.stringify(newFilters.selectedTags) !== JSON.stringify(videoSelectedTags || []) ||
      newFilters.selectedRegion !== (videoSelectedRegion || '') ||
      newFilters.rankingMethod !== (videoRankingMethod || 'views') ||
      newFilters.timeWindow !== (videoTimeWindow || 'week')
    );
    
    setHasVideoChanges(hasChanges);
  };

  const handleChannelFilterChange = (filterName, value) => {
    const newFilters = { ...localChannelFilters, [filterName]: value };
    
    if (filterName === 'selectedCategory') {
      newFilters.selectedSubcategory = '';
    }
    
    setLocalChannelFilters(newFilters);
    
    const hasChanges = (
      newFilters.selectedCategory !== (channelSelectedCategory || '') ||
      newFilters.selectedSubcategory !== (channelSelectedSubcategory || '') ||
      newFilters.selectedRegion !== (channelSelectedRegion || '') ||
      newFilters.rankingMethod !== (channelRankingMethod || 'subscribers') ||
      JSON.stringify(newFilters.selectedTags) !== JSON.stringify(channelSelectedTags || [])
    );
    
    setHasChannelChanges(hasChanges);
  };

  const applyVideoFilters = () => {
    if (!videoDiscoveryContext) return;
    
    setVideoSelectedCategory(localVideoFilters.selectedCategory || null);
    setVideoSelectedFormat(localVideoFilters.selectedFormat || null);
    setVideoSelectedTags(localVideoFilters.selectedTags);
    setVideoSelectedRegion(localVideoFilters.selectedRegion || null);
    setVideoRankingMethod(localVideoFilters.rankingMethod);
    setVideoTimeWindow(localVideoFilters.timeWindow);
    
    setHasVideoChanges(false);
    
    if (isMobile) {
      setIsMobileOpen(false);
    }
  };

  const applyChannelFilters = () => {
    if (!channelDiscoveryContext) return;
    
    setChannelSelectedCategory(localChannelFilters.selectedCategory || null);
    setChannelSelectedSubcategory(localChannelFilters.selectedSubcategory || null);
    setChannelSelectedRegion(localChannelFilters.selectedRegion || null);
    setChannelRankingMethod(localChannelFilters.rankingMethod);
    setChannelSelectedTags(localChannelFilters.selectedTags);
    
    setHasChannelChanges(false);
    
    if (isMobile) {
      setIsMobileOpen(false);
    }
  };

  const resetVideoFilters = () => {
    const defaultFilters = {
      selectedCategory: '',
      selectedFormat: '',
      selectedTags: [],
      selectedRegion: '',
      rankingMethod: 'views',
      timeWindow: 'week'
    };
    setLocalVideoFilters(defaultFilters);
    setHasVideoChanges(true);
  };

  const resetChannelFilters = () => {
    const defaultFilters = {
      selectedCategory: '',
      selectedSubcategory: '',
      selectedRegion: '',
      rankingMethod: 'subscribers',
      selectedTags: []
    };
    setLocalChannelFilters(defaultFilters);
    setHasChannelChanges(true);
  };

  // ✅ ИСПРАВЛЕНИЕ: Используем callback для уведомления Layout
  const toggleSidebar = () => {
    const newCollapsed = !isCollapsed;
    
    // Уведомляем родительский Layout о изменении состояния
    if (onCollapsedChange) {
      onCollapsedChange(newCollapsed);
    }
    
    setTimeout(() => setForceUpdate(prev => prev + 1), 300);
  };

  const toggleMobileSidebar = () => {
    setIsMobileOpen(!isMobileOpen);
    setTimeout(() => setForceUpdate(prev => prev + 1), 100);
  };

  // Автозакрытие мобильного меню при смене маршрута
  useEffect(() => {
    if (isMobile) {
      setIsMobileOpen(false);
    }
  }, [location.pathname, isMobile]);

  // Теги для фильтров
  const videoPopularTags = [
    'Music Video', 'Tutorial', 'Review', 'Vlog', 'Gameplay',
    'Technology', 'Comedy', 'Education', 'Travel', 'Cooking'
  ];

  const channelPopularTags = [
    'Gaming', 'Technology', 'Education', 'Music', 'Comedy',
    'Science', 'Programming', 'Review', 'Tutorial', 'Entertainment',
    'News', 'Sports', 'Cooking', 'Travel', 'Fitness'
  ];

  // Рендер фильтров (без изменений - весь существующий код)
  const renderVideoDiscoveryFilters = () => {
    const shouldShowFilters = location.pathname.startsWith('/video-discovery') && 
                            (isMobile ? isMobileOpen : !isCollapsed);

    if (!shouldShowFilters) {
      return null;
    }

    return (
      <div className="sidebar__filters" key={`video-filters-${forceUpdate}`}>
        <div className="sidebar__filter-item">
          <VideoRegionSelector
            selectedRegion={localVideoFilters.selectedRegion}
            onChange={(region) => handleVideoFilterChange('selectedRegion', region)}
            disabled={isVideoLoading}
          />
        </div>

        <div className="sidebar__filter-item">
          <VideoCategorySelector
            categories={videoCategories || {}}
            selectedCategory={localVideoFilters.selectedCategory}
            selectedFormat={localVideoFilters.selectedFormat}
            onCategoryChange={(category) => handleVideoFilterChange('selectedCategory', category)}
            onFormatChange={(format) => handleVideoFilterChange('selectedFormat', format)}
            disabled={isVideoLoading}
          />
        </div>

        <div className="sidebar__filter-item">
          <VideoRankingMethodSelector
            rankingMethod={localVideoFilters.rankingMethod}
            onRankingMethodChange={(method) => handleVideoFilterChange('rankingMethod', method)}
            disabled={isVideoLoading}
          />
        </div>

        <div className="sidebar__filter-item">
          <TimeWindowSelector
            timeWindow={localVideoFilters.timeWindow}
            onChange={(window) => handleVideoFilterChange('timeWindow', window)}
            disabled={isVideoLoading}
          />
        </div>

        <div className="sidebar__filter-item">
          <VideoTagsFilter
            selectedTags={localVideoFilters.selectedTags}
            onTagsChange={(tags) => handleVideoFilterChange('selectedTags', tags)}
            popularTags={videoPopularTags}
            disabled={isVideoLoading}
          />
        </div>

        <div className="sidebar__filter-actions">
          <button
            className={`sidebar__filter-button ${
              isVideoLoading 
                ? 'sidebar__filter-button--loading'
                : hasVideoChanges 
                  ? 'sidebar__filter-button--primary'
                  : 'sidebar__filter-button--disabled'
            }`}
            onClick={applyVideoFilters}
            disabled={!hasVideoChanges || isVideoLoading}
          >
            {isVideoLoading ? (
              <>🔄 Загружаем видео...</>
            ) : hasVideoChanges ? (
              <>🔄 Применить фильтры</>
            ) : (
              <>✓ Фильтры применены</>
            )}
          </button>
        </div>
      </div>
    );
  };

  const renderChannelDiscoveryFilters = () => {
    const shouldShowFilters = location.pathname.startsWith('/channel-discovery') && 
                            (isMobile ? isMobileOpen : !isCollapsed);

    if (!shouldShowFilters) {
      return null;
    }

    return (
      <div className="sidebar__filters" key={`channel-filters-${forceUpdate}`}>
        <div className="sidebar__filter-item">
          <ChannelRegionSelector
            selectedRegion={localChannelFilters.selectedRegion}
            onChange={(region) => handleChannelFilterChange('selectedRegion', region)}
            disabled={isChannelLoading}
          />
        </div>

        <div className="sidebar__filter-item">
          <CategorySelector
            categories={channelCategories || {}}
            selectedCategory={localChannelFilters.selectedCategory}
            selectedSubcategory={localChannelFilters.selectedSubcategory}
            onCategoryChange={(category) => handleChannelFilterChange('selectedCategory', category)}
            onSubcategoryChange={(subcategory) => handleChannelFilterChange('selectedSubcategory', subcategory)}
            disabled={isChannelLoading}
          />
        </div>

        <div className="sidebar__filter-item">
          <RankingMethodSelector
            rankingMethod={localChannelFilters.rankingMethod}
            onRankingMethodChange={(method) => handleChannelFilterChange('rankingMethod', method)}
            disabled={isChannelLoading}
          />
        </div>

        <div className="sidebar__filter-item">
          <TagsFilter
            selectedTags={localChannelFilters.selectedTags}
            onTagsChange={(tags) => handleChannelFilterChange('selectedTags', tags)}
            popularTags={channelPopularTags}
            disabled={isChannelLoading}
          />
        </div>

        <div className="sidebar__filter-actions">
          <button
            className={`sidebar__filter-button ${
              isChannelLoading 
                ? 'sidebar__filter-button--loading'
                : hasChannelChanges 
                  ? 'sidebar__filter-button--primary'
                  : 'sidebar__filter-button--disabled'
            }`}
            onClick={applyChannelFilters}
            disabled={!hasChannelChanges || isChannelLoading}
          >
            {isChannelLoading ? (
              <>🔄 Загружаем каналы...</>
            ) : hasChannelChanges ? (
              <>🔄 Применить фильтры</>
            ) : (
              <>✓ Фильтры применены</>
            )}
          </button>
        </div>
      </div>
    );
  };

  // ✅ НОВЫЙ: Формируем классы с учетом новой архитектуры
  const getSidebarClasses = () => {
    const classes = ['sidebar'];
    
    // Управление видимостью
    if (!isVisible) {
      classes.push('sidebar--hidden');
    } else {
      classes.push('sidebar--visible');
    }
    
    // Управление сворачиванием - используем ВНЕШНЕЕ состояние
    if (isCollapsed && !isMobile) {
      classes.push('sidebar--collapsed');
    }
    
    // Мобильное состояние
    if (isMobile && isMobileOpen) {
      classes.push('sidebar--mobile-open');
    }
    
    return classes.join(' ');
  };

  // ✅ УСЛОВНЫЙ РЕНДЕР: Если sidebar не должен быть виден, рендерим null для десктопа
  if (!isVisible && !isMobile) {
    return null;
  }

  return (
    <>
      {/* Мобильная кнопка */}
      {isMobile && (
        <button 
          className="sidebar-mobile-toggle"
          onClick={toggleMobileSidebar}
          aria-label={isMobileOpen ? 'Закрыть меню' : 'Открыть меню'}
        >
          {isMobileOpen ? <PiX /> : <PiList />}
        </button>
      )}

      {/* Мобильный overlay */}
      {isMobile && isMobileOpen && (
        <div 
          className="sidebar-overlay sidebar-overlay--visible"
          onClick={toggleMobileSidebar}
          aria-hidden="true"
        />
      )}

      {/* Основной sidebar */}
      <aside 
        className={getSidebarClasses()} 
        key={`sidebar-${forceUpdate}`}
        aria-hidden={!isVisible}
      >
        {/* Кнопка сворачивания только для десктопа */}
        {!isMobile && (
          <div className="sidebar__toggle" onClick={toggleSidebar}>
            {isCollapsed ? '›' : '‹'}
          </div>
        )}

        <nav className="sidebar__nav" ref={navRef}>
          <ul className="sidebar__nav-list">
            <li className="sidebar__nav-item sidebar__nav-item--expandable">
              <div 
                className={`sidebar__nav-link sidebar__nav-header ${researchExpanded ? 'sidebar__nav-header--expanded' : ''}`}
                onClick={() => (isMobile || !isCollapsed) && setResearchExpanded(!researchExpanded)}
              >
                <PiChartBar className="sidebar__nav-icon" />
                {(isMobile || !isCollapsed) && (
                  <>
                    <span className="sidebar__nav-text">
                      {t('navigation.research', 'Research')}
                    </span>
                    <FaChevronDown 
                      className={`sidebar__nav-arrow ${researchExpanded ? 'sidebar__nav-arrow--expanded' : ''}`} 
                    />
                  </>
                )}
              </div>
              
              {researchExpanded && (isMobile || !isCollapsed) && (
                <ul className="sidebar__nav-submenu">
                  <li className="sidebar__nav-subitem">
                    <NavLink 
                      to="/research/dashboard" 
                      className={({ isActive }) => 
                        `sidebar__nav-sublink ${isActive ? 'sidebar__nav-sublink--active' : ''}`
                      }
                    >
                      <span className="sidebar__nav-subtext">
                        {t('navigation.ytDashboard', 'YT Dashboard')}
                      </span>
                    </NavLink>
                  </li>
                  <li className="sidebar__nav-subitem">
                    <NavLink 
                      to="/research/most-viewed" 
                      className={({ isActive }) => 
                        `sidebar__nav-sublink ${isActive ? 'sidebar__nav-sublink--active' : ''}`
                      }
                    >
                      <span className="sidebar__nav-subtext">
                        {t('navigation.mostViewed', 'Most Viewed')}
                      </span>
                    </NavLink>
                  </li>
                  <li className="sidebar__nav-subitem">
                    <NavLink 
                      to="/research/keywords" 
                      className={({ isActive }) => 
                        `sidebar__nav-sublink ${isActive ? 'sidebar__nav-sublink--active' : ''}`
                      }
                    >
                      <span className="sidebar__nav-subtext">
                        {t('navigation.keywords', 'Keywords')}
                      </span>
                    </NavLink>
                  </li>
                  <li className="sidebar__nav-subitem">
                    <NavLink 
                      to="/research/monetization" 
                      className={({ isActive }) => 
                        `sidebar__nav-sublink ${isActive ? 'sidebar__nav-sublink--active' : ''}`
                      }
                    >
                      <span className="sidebar__nav-subtext">
                        {t('navigation.monetization', 'Monetization')} 
                      </span>
                    </NavLink>
                  </li>
                </ul>
              )}
            </li>

            <li className="sidebar__nav-item">
              <NavLink
                to="/channel-discovery"
                className={({ isActive }) =>
                  `sidebar__nav-link ${isActive ? 'sidebar__nav-link--active' : ''}`
                }
              >
                <PiUserCircle className="sidebar__nav-icon" />
                {(isMobile || !isCollapsed) && <span className="sidebar__nav-text">{t('navigation.channelDiscovery')}</span>}
              </NavLink>
            </li>

            {renderChannelDiscoveryFilters()}

            <li className="sidebar__nav-item">
              <NavLink
                to="/video-discovery"
                className={({ isActive }) =>
                  `sidebar__nav-link ${isActive ? 'sidebar__nav-link--active' : ''}`
                }
              >
                <PiVideoCamera className="sidebar__nav-icon" />
                {(isMobile || !isCollapsed) && <span className="sidebar__nav-text">{t('navigation.videoDiscovery')}</span>}
              </NavLink>
            </li>

            {renderVideoDiscoveryFilters()}

            <li className="sidebar__nav-item">
              <NavLink
                to="/channel-analysis"
                className={({ isActive }) =>
                  `sidebar__nav-link ${isActive ? 'sidebar__nav-link--active' : ''}`
                }
              >
                <PiFolderUser className="sidebar__nav-icon" />
                {(isMobile || !isCollapsed) && <span className="sidebar__nav-text">{t('navigation.channelAnalysis')}</span>}
              </NavLink>
            </li>

            <li className="sidebar__nav-item">
              <NavLink
                to="/video-analysis"
                className={({ isActive }) =>
                  `sidebar__nav-link ${isActive ? 'sidebar__nav-link--active' : ''}`
                }
              >
                <PiFileVideoLight className="sidebar__nav-icon" />
                {(isMobile || !isCollapsed) && <span className="sidebar__nav-text">{t('navigation.videoAnalysis')}</span>}
              </NavLink>
            </li>
          </ul>

          <div className="sidebar__bottom-section">
            <ul className="sidebar__nav-list sidebar__nav-list--bottom">
              <li className="sidebar__nav-item">
                <NavLink
                  to="/marketplace"
                  className={({ isActive }) =>
                    `sidebar__nav-link sidebar__nav-link--bottom ${isActive ? 'sidebar__nav-link--active' : ''}`
                  }
                >
                  <PiStorefront className="sidebar__nav-icon" />
                  {(isMobile || !isCollapsed) && <span className="sidebar__nav-text">{t('navigation.marketplace', 'MarketPlace')}</span>}
                </NavLink>
              </li>

              <li className="sidebar__nav-item">
                <NavLink
                  to="/pricing"
                  className={({ isActive }) =>
                    `sidebar__nav-link sidebar__nav-link--bottom ${isActive ? 'sidebar__nav-link--active' : ''}`
                  }
                >
                  <PiCrown className="sidebar__nav-icon" />
                  {(isMobile || !isCollapsed) && <span className="sidebar__nav-text">Upgrade Plan</span>}
                </NavLink>
              </li>

              <li className="sidebar__nav-item">
                <NavLink
                  to="/knowledge"
                  className={({ isActive }) =>
                    `sidebar__nav-link sidebar__nav-link--bottom ${isActive ? 'sidebar__nav-link--active' : ''}`
                  }
                >
                  <PiBookOpen className="sidebar__nav-icon" />
                  {(isMobile || !isCollapsed) && <span className="sidebar__nav-text">Community</span>}
                </NavLink>
              </li>

              <li className="sidebar__nav-item">
                <NavLink
                  to="/help"
                  className={({ isActive }) =>
                    `sidebar__nav-link sidebar__nav-link--bottom ${isActive ? 'sidebar__nav-link--active' : ''}`
                  }
                >
                  <PiQuestion className="sidebar__nav-icon" />
                  {(isMobile || !isCollapsed) && <span className="sidebar__nav-text">Help</span>}
                </NavLink>
              </li>
            </ul>
          </div>
        </nav>
      </aside>
    </>
  );
};

export default Sidebar;